/** Navigation guard: unauthenticated → /login?redirect=…; admin-only routes; login page when logged in. */
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { safeRedirect } from './redirect';

declare module 'vue-router' {
  interface RouteMeta {
    /** reachable without login */
    public?: boolean;
    /** admins only (others get the 404 page) */
    admin?: boolean;
    /** rendered as overlay above the parent route */
    overlay?: boolean;
  }
}

export interface GuardSession {
  ensure(): Promise<boolean>;
  readonly me: { isAdmin: boolean } | null;
}

export async function authGuard(to: RouteLocationNormalized, session: GuardSession): Promise<true | RouteLocationRaw> {
  let authenticated = false;
  try {
    authenticated = await session.ensure();
  } catch {
    // API unreachable: public pages still work, others go to the login page (which shows the error)
  }
  if (to.meta.public) {
    if (to.name === 'login' && authenticated) return safeRedirect(to.query.redirect);
    return true;
  }
  if (!authenticated) return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} };
  if (to.meta.admin && !session.me?.isAdmin) return { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } };
  return true;
}
